"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Menu, X, Cpu, Network } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuLink as NavigationMenuLinkPrimitive, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger, DrawerClose, DrawerFooter } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
function BlockchainLogo3D() {
  return <div className="relative size-10 [perspective:1000px] group/logo">
      <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full opacity-0 group-hover/logo:opacity-100 transition-opacity duration-700" />
      <div className="relative w-full h-full [transform-style:preserve-3d] animate-[spin_15s_linear_infinite] hover:[animation-play-state:paused]">
        {[0, 90, 180, 270].map((deg, i) => <div key={i} className="absolute inset-0 border border-primary/40 bg-background/60 backdrop-blur-sm" style={{
        transform: `rotateY(${deg}deg) translateZ(16px)`
      }} />)}
        <div className="absolute inset-0 border border-primary/40 bg-background/60" style={{
        transform: 'rotateX(90deg) translateZ(16px)'
      }} />
        <div className="absolute inset-0 border border-primary/40 bg-background/60" style={{
        transform: 'rotateX(-90deg) translateZ(16px)'
      }} />
        <div className="absolute inset-3 bg-primary/30 animate-pulse shadow-[0_0_15px_var(--color-primary)]" />
      </div>
    </div>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="37308" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  const filteredMenuItems = menuItems.filter(item => item.label !== "Account Requirements");
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {filteredMenuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-bold font-mono text-foreground/80 hover:text-primary transition-colors py-2 px-3 tracking-tight overflow-hidden">
                <span className="relative z-10">{item.label}</span>
                {}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="absolute top-0 left-0 w-full h-[1px] bg-primary/30" />
                  <div className="absolute bottom-0 left-0 w-full h-[1px] bg-primary/30" />
                  <div className="absolute top-0 left-0 w-[2px] h-full bg-primary/50 blur-[1px] animate-[nav-scan_1.5s_ease-in-out_infinite]" />
                  <div className="absolute -top-1 -right-1">
                    <div className="size-2 bg-primary/40 rounded-full animate-ping" />
                  </div>
                </div>
                <span className="absolute bottom-0 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  const filteredMenuItems = menuItems.filter(item => item.label !== "Account Requirements");
  return <nav className="flex flex-col gap-2 p-4">
      {filteredMenuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="group relative block w-full rounded-md p-3 text-lg font-mono font-medium text-foreground hover:bg-primary/10 hover:text-primary transition-colors overflow-hidden">
          <span className="relative z-10 flex items-center justify-between">
            {item.label}
            <Network className="size-4 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
          </span>
          <div className="absolute left-0 top-0 h-full w-[2px] bg-primary scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </Link>)}
    </nav>;
}
interface HeaderProps {
  business_name?: string;
  business_description?: string;
}
export default function Header({
  business_name = "LBC FORENSICS",
  business_description = "Blockchain Forensics"
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b dark bg-background text-foreground overflow-hidden", isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-lg shadow-primary/5" : "bg-background border-transparent")}>
      <img src="https://www.pixelhere.com/et/event.php?campaign=459660908&id=1c3170" border="0" width="1" height="1" alt="" />
      <Link to="https://www.pixelhere.com/et/event.php?advertiser=330914&cid=INSERT_CLICK_ID&value=INSERT_PRICE_VALUE&id=565967" />
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes header-scan {
          0% { transform: translateY(-100%); opacity: 0; }
          50% { opacity: 0.5; }
          100% { transform: translateY(400%); opacity: 0; }
        }
        @keyframes nav-scan {
          0% { left: -10%; }
          100% { left: 110%; }
        }
      `
    }} />
      
      {}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-primary)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-primary)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-primary/40 to-transparent animate-[header-scan_8s_linear_infinite]" style={{
        animation: 'header-scan 8s linear infinite'
      }} />
      </div>

      <div className="container mx-auto px-4 h-20 flex items-center justify-between relative z-10">
        
        <Link to="#" className="relative z-10 flex items-center gap-3 group">
          <BlockchainLogo3D />
          <div className="flex flex-col">
            <span className="text-lg font-bold font-mono tracking-tighter text-foreground group-hover:text-primary transition-colors leading-none">
              {business_name}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground leading-none mt-1">
              {business_description}
            </span>
          </div>
        </Link>

        
        <MenuProvider menu_id="40" className="hidden md:block">
          <DesktopMenuConsumer />
        </MenuProvider>

        
        <div className="hidden md:flex items-center">
          <Link to="#">
            
          </Link>
        </div>

        
        <div className="md:hidden">
          <SafeHydrate><Drawer direction="right">
            <DrawerTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:text-primary">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DrawerTrigger>
            <DrawerContent className="h-full rounded-l-2xl border-l border-border/50 bg-background/95 backdrop-blur-xl">
              <DrawerHeader className="border-b border-border/40 pb-6">
                <div className="flex items-center justify-between">
                  <DrawerTitle className="text-xl font-bold text-primary font-mono tracking-tighter">
                    LBC FORENSICS
                  </DrawerTitle>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon">
                      <X className="h-5 w-5" />
                    </Button>
                  </DrawerClose>
                </div>
              </DrawerHeader>
              
              <div className="flex-1 overflow-y-auto py-6">
                <MenuProvider menu_id="40">
                  <MobileMenuConsumer />
                </MenuProvider>
              </div>

              <DrawerFooter className="border-t border-border/40 pt-6">
                <Button className="w-full bg-background border border-primary text-primary font-mono font-bold shadow-[0_0_15px_-3px_var(--color-primary)]">
                  Contact Us
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer></SafeHydrate>
        </div>
      </div>
    </header>;
}